import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  App, Card, Descriptions, Table, Button, Spin, Result, Space, Tag, Input, Timeline, Row, Col, Divider, Typography,
} from 'antd';
import {
  ArrowLeftOutlined, SendOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { QUOTE_STATUS_COLORS, getQuoteStatusLabel } from '../utils/quoteUtils';
import { formatCurrency, formatDateTime } from '../utils/format';

interface QuoteMessage {
  id: string;
  text: string;
  sender_type: string;
  sender?: { first_name?: string; last_name?: string; email?: string };
  customer?: { first_name?: string; last_name?: string; email?: string };
  service_provider_admin?: { first_name?: string; last_name?: string; email?: string };
  item_id?: string;
  created_at: string;
}

interface QuoteItem {
  id: string;
  title: string;
  thumbnail?: string;
  sku?: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface Quote {
  id: string;
  quote_number?: number;
  status: string;
  notes?: string;
  scheduled_for?: string;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  draft_order?: {
    id: string;
    display_id: string;
    items: QuoteItem[];
    subtotal: number;
    discount_total: number;
    shipping_total: number;
    tax_total: number;
    total: number;
    original_total: number;
    currency_code: string;
  };
  service_provider?: {
    id: string;
    name: string;
  };
  messages?: QuoteMessage[];
  created_at: string;
}

const QuoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadQuote();
  }, [id]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/quotes/${id}`);
      setQuote(response.data.quote || response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(error.response?.data?.message || t('common.noData'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getMessageSenderLabel = (msg: QuoteMessage): string => {
    const person = msg.customer || msg.service_provider_admin;
    if (!person) return '-';
    const role = msg.customer ? t('quotes.customer') : t('quotes.serviceProvider');
    const name = [person.first_name, person.last_name].filter(Boolean).join(' ');
    return `${name || person.email || '-'} (${role})`;
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      setSendingMessage(true);
      await apiClient.post(`/admin/quotes/${id}/messages`, { text: messageText });
      setMessageText('');
      loadQuote();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('quotes.sendMessageError'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendQuote = () => {
    modal.confirm({
      title: t('quotes.sendQuote'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      onOk: async () => {
        try {
          setActionLoading(true);
          await apiClient.post(`/admin/quotes/${id}/send`);
          message.success(t('quotes.sendSuccess'));
          loadQuote();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('quotes.sendError'));
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRejectQuote = () => {
    modal.confirm({
      title: t('quotes.rejectQuote'),
      icon: <ExclamationCircleOutlined />,
      content: t('common.confirm'),
      okText: t('common.yes'),
      cancelText: t('common.no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setActionLoading(true);
          await apiClient.post(`/admin/quotes/${id}/reject`);
          message.success(t('quotes.rejectSuccess'));
          loadQuote();
        } catch (error: any) {
          message.error(error.response?.data?.message || t('quotes.rejectError'));
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !quote) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t('common.noData')}
        extra={
          <Button type="primary" onClick={() => navigate('/quotes')}>
            {t('common.back')}
          </Button>
        }
      />
    );
  }

  const currency = quote.draft_order?.currency_code || 'EUR';
  const canSendOrReject =
    quote.status === 'pending_service_provider' || quote.status === 'customer_rejected';

  const itemColumns = [
    {
      title: t('products.name'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('quotes.sku'),
      dataIndex: 'sku',
      key: 'sku',
      render: (sku: string) => sku || '-',
    },
    {
      title: t('quotes.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: t('quotes.unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (v: number) => formatCurrency(v, currency, i18n.language),
    },
    {
      title: t('common.total'),
      dataIndex: 'total',
      key: 'total',
      render: (v: number) => formatCurrency(v, currency, i18n.language),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/quotes')}>
          {t('common.back')}
        </Button>
      </Space>

      <Card
        title={
          <Space>
            <span>{t('quotes.detail')} - {quote.quote_number || quote.draft_order?.display_id || quote.id}</span>
            <Tag color={QUOTE_STATUS_COLORS[quote.status] || 'default'}>
              {getQuoteStatusLabel(quote.status, t)}
            </Tag>
          </Space>
        }
        extra={
          canSendOrReject && (
            <Space>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendQuote}
                loading={actionLoading}
              >
                {t('quotes.sendQuote')}
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleRejectQuote}
                loading={actionLoading}
              >
                {t('quotes.rejectQuote')}
              </Button>
            </Space>
          )
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card type="inner" title={t('quotes.items')} style={{ marginBottom: 16 }}>
              <Table
                columns={itemColumns}
                dataSource={quote.draft_order?.items || []}
                rowKey="id"
                pagination={false}
                size="small"
              />

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <Descriptions column={1} size="small" style={{ minWidth: 250 }}>
                  <Descriptions.Item label={t('quotes.subtotal')}>
                    {formatCurrency(quote.draft_order?.subtotal, currency, i18n.language)}
                  </Descriptions.Item>
                  {(quote.draft_order?.discount_total || 0) > 0 && (
                    <Descriptions.Item label={t('quotes.discounts')}>
                      -{formatCurrency(quote.draft_order?.discount_total, currency, i18n.language)}
                    </Descriptions.Item>
                  )}
                  {(quote.draft_order?.shipping_total || 0) > 0 && (
                    <Descriptions.Item label={t('quotes.shipping')}>
                      {formatCurrency(quote.draft_order?.shipping_total, currency, i18n.language)}
                    </Descriptions.Item>
                  )}
                  {(quote.draft_order?.tax_total || 0) > 0 && (
                    <Descriptions.Item label={t('quotes.tax')}>
                      {formatCurrency(quote.draft_order?.tax_total, currency, i18n.language)}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t('common.total')}>
                    {quote.draft_order?.original_total &&
                      quote.draft_order.original_total !== quote.draft_order.total && (
                        <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 8 }}>
                          {formatCurrency(quote.draft_order.original_total, currency, i18n.language)}
                        </span>
                      )}
                    <span style={{ fontWeight: 600, color: '#BD7423' }}>
                      {formatCurrency(quote.draft_order?.total, currency, i18n.language)}
                    </span>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </Card>

            <Card type="inner" title={t('quotes.messages')}>
              {(quote.messages || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
                  {t('quotes.noMessages')}
                </div>
              ) : (
                <Timeline
                  items={(quote.messages || []).map((msg) => ({
                    color: msg.sender_type === 'customer' ? 'blue' : 'green',
                    children: (
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {getMessageSenderLabel(msg)}
                          <span style={{ marginLeft: 8 }}>
                            {formatDateTime(msg.created_at, i18n.language)}
                          </span>
                        </Typography.Text>
                        <div style={{ marginTop: 4 }}>{msg.text}</div>
                        {msg.item_id && (
                          <Tag style={{ marginTop: 4, fontSize: 12 }}>
                            Item: {msg.item_id}
                          </Tag>
                        )}
                      </div>
                    ),
                  }))}
                />
              )}

              <Divider />

              <div style={{ display: 'flex', gap: 8 }}>
                <Input.TextArea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder={t('quotes.typeMessage')}
                  rows={2}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={sendingMessage}
                  disabled={!messageText.trim()}
                >
                  {t('quotes.send')}
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card type="inner" title={t('quotes.customer')} style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('common.name')}>
                  {quote.customer
                    ? `${quote.customer.first_name} ${quote.customer.last_name}`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('common.email')}>
                  {quote.customer?.email || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card type="inner" title={t('quotes.serviceProvider')} style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('common.name')}>
                  {quote.service_provider?.name || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {quote.notes && (
              <Card type="inner" title={t('quotes.notes')} style={{ marginBottom: 16 }}>
                <p>{quote.notes}</p>
              </Card>
            )}

            {quote.scheduled_for && (
              <Card type="inner" title={t('quotes.scheduledFor')} style={{ marginBottom: 16 }}>
                <p>{formatDateTime(quote.scheduled_for, i18n.language)}</p>
              </Card>
            )}

            {quote.status === 'accepted' && quote.draft_order?.id && (
              <Button
                type="primary"
                icon={<ShoppingCartOutlined />}
                block
                onClick={() => navigate(`/orders/${quote.draft_order?.id}`)}
              >
                {t('quotes.viewOrder')}
              </Button>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default QuoteDetail;
